/**
 * PATH: apps/qa/src/pages/reports/EmailReportDialog.tsx
 * PURPOSE: Modal dialog for emailing QA reports to recipients
 * WHY: Extracted from ReportsPage.tsx to keep files under 300 lines
 */

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { reportApi } from '@/lib/api'
import type { Report } from '@/types/qa'
import {
  X,
  CheckCircle,
  Loader2,
  Share2,
  AlertTriangle,
} from 'lucide-react'

interface EmailDialogProps {
  report: Report
  isOpen: boolean
  onClose: () => void
}

export function EmailReportDialog({ report, isOpen, onClose }: EmailDialogProps) {
  const [toEmails, setToEmails] = useState('')
  const [ccEmails, setCcEmails] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [attachPdf, setAttachPdf] = useState(true)
  const [sent, setSent] = useState(false)
  const [pdfAttached, setPdfAttached] = useState(false)

  const emailMutation = useMutation({
    mutationFn: async () => {
      const toList = toEmails.split(',').map(e => e.trim()).filter(Boolean)
      const ccList = ccEmails ? ccEmails.split(',').map(e => e.trim()).filter(Boolean) : undefined
      return reportApi.emailReport(report.id, {
        to: toList,
        cc: ccList,
        subject: subject || undefined,
        message: message || undefined,
        attach_pdf: attachPdf,
      })
    },
    onSuccess: (result) => {
      setSent(true)
      setPdfAttached(result.pdf_attached)
    },
  })

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div role="dialog" aria-modal="true" aria-labelledby="email-report-title" className="bg-card rounded-xl p-6 w-full max-w-md shadow-xl border border-border animate-fadeIn">
        <div className="flex items-center justify-between mb-4">
          <h3 id="email-report-title" className="text-lg font-semibold text-foreground">Email issued revision</h3>
          <button
            onClick={() => { onClose(); setSent(false); }}
            className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {sent ? (
          <div className="text-center py-6">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-foreground font-medium">Email queued successfully</p>
            <p className="text-sm text-muted-foreground mt-1">
              Email queued for {toEmails.split(',').length} recipient(s)
            </p>
            <p className="text-xs text-muted-foreground mt-1">{pdfAttached ? 'Fixed issued PDF attached.' : 'Email queued without a PDF attachment.'}</p>
            <button
              onClick={() => { onClose(); setSent(false); }}
              className="mt-4 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                To <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={toEmails}
                onChange={(e) => setToEmails(e.target.value)}
                placeholder="email@example.com, another@example.com"
                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <p className="text-xs text-muted-foreground mt-0.5">Separate multiple with commas</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">CC</label>
              <input
                type="text"
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                placeholder="cc@example.com"
                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={`QA Report: ${report?.report_name || ''}`}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Please find the attached QA report..."
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={attachPdf}
                onChange={(e) => setAttachPdf(e.target.checked)}
                className="rounded border-input"
              />
              Attach PDF report
            </label>

            {emailMutation.isError && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                Failed to send. Please try again.
              </p>
            )}

            <button
              onClick={() => emailMutation.mutate()}
              disabled={emailMutation.isPending || !toEmails.trim()}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {emailMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Share2 className="w-4 h-4" />
                  Send Email
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
